import * as React from 'react'
import { Formik } from 'formik'
import styled from '@theme/styled-components'

import rights from '@utils/RightManager'
import Logger from '@utils/Logger'

import Select from '@components/select/select'
import RegularFilters from '@components/filter/regularFilters'
import { initialFilters } from '@components/filter/filterUtils'
import Button from '@components/button/button'

import * as AccountStore from '@store/account/account'
import * as FiltersStore from '@store/filters/filters'
import useI18n from '@store/i18n/useI18n'

import apis from '@api/api'
import { buildParamsAdminBusiness } from '@utils/buildParamsWS'

interface Props {
  screenStatus: ScreenStatus
  setScreenStatus: (s: ScreenStatus) => void
  setBusinessesBoard: (board: AdminBusinessesExtended) => void
  setExportQuery: (query: string) => void
  currentPage: number
}

const MonitorFilters = ({ screenStatus, setScreenStatus, setBusinessesBoard, setExportQuery, currentPage }: Props) => {
  const i18n = useI18n()
  const account = AccountStore.store.getState().account
  const regionsOptions: Option[] | undefined = account && account.regions
  const [businessesNumber, setBusinessesNumber] = React.useState(0)

  const initialValues = React.useMemo(
    () => FiltersStore.store.getState().businessesFilters || initialFilters(regionsOptions),
    []
  )

  const fetchBusinesses = (values: BusinessesFilters) => {
    setScreenStatus('loading')
    apis.admin.business
      .board(buildParamsAdminBusiness(values, 'search', businessesNumber, currentPage))
      .then((response) => {
        setBusinessesBoard(response)
        setScreenStatus('fetched')
        setExportQuery(buildParamsAdminBusiness(values, 'export', businessesNumber, currentPage))
        setBusinessesNumber(response.size)
      })
      .catch((err) => {
        Logger.error(err)
        setScreenStatus('error')
      })
  }
  return (
    <Formik initialValues={initialValues} onSubmit={fetchBusinesses}>
      {({ values, setFieldValue, handleSubmit }) => {
        const changeAndSubmit = (fieldName: string, e: any) => {
          if (fieldName === 'company') {
            setFieldValue('companyGroup', undefined)
          } else if (fieldName === 'companyGroup') {
            setFieldValue('company', undefined)
          }
          setFieldValue(fieldName, e)
        }

        React.useEffect(() => {
          fetchBusinesses(values as BusinessesFilters)
        }, [currentPage])

        React.useEffect(() => {
          setExportQuery(buildParamsAdminBusiness(values as BusinessesFilters, 'export', businessesNumber, currentPage))
        }, [])

        return (
          <>
            {regionsOptions && regionsOptions.length !== 0 && rights.canFilterOnRegion() && (
              <SelectContainer>
                <Select
                  options={regionsOptions}
                  selected={values.region}
                  setSelected={(e) => changeAndSubmit('region', e)}
                  placeholder={i18n.t('components.menu.placeholder.regions')}
                  isClearable={false}
                  noMargin
                />
              </SelectContainer>
            )}
            <RegularFilters
              values={values}
              changeAndSubmit={changeAndSubmit}
              setScreenStatus={setScreenStatus}
              mode="board"
            />
            <ButtonContainer>
              <Button
                title={i18n.t('components.monitorByRegion.displayBusinesses')}
                onClick={handleSubmit}
                type="action"
                status={screenStatus === 'loading' ? 'loading' : 'active'}
              />
            </ButtonContainer>
          </>
        )
      }}
    </Formik>
  )
}
export default MonitorFilters

const SelectContainer = styled('div')`
  padding: 10px 0;
  border: 0px solid ${(props) => props.theme.colors.menuBorder};
  border-bottom-width: 1px;
  border-top-width: 1px;
`
const ButtonContainer = styled('div')`
  display: flex;
  padding-top: 15px;
  justify-content: center;
  align-items: center;
`
